import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { noAuthGuard } from './core/guards/noAuth.guard';
import { LayoutComponent } from './layouts/layout.component';
import { DataResolver } from './core/resolver/data.resolver';
import { appCodeRedirectGuard } from './core/guards/app-code-redirect.guard';
import { guestGuard } from './core/guards/guest.guard';
import { documentsContextGuard } from './core/guards/documents-context.guard';
import { ScreenRenderingMode } from './core/const/constants';
import { devtoolGuard } from './core/guards/devtool.guard';
import { signalRResolver } from './core/resolver/signal-r.resolver';
export const routes: Routes = [
  // Dev tools popup route — opened only as popup window in dev environments
  {
    path: 'devtool',
    canActivate: [devtoolGuard],
    resolve: { resolvedData: DataResolver },
    loadComponent: () => import('./shared/dev-tool/devtool-popup/devtool-popup.component').then(m => m.DevToolPopupComponent)
  },

  {
    path: '',
    pathMatch: 'full',
    canActivate: [appCodeRedirectGuard],
    children: [] // This empty children array is necessary for the canActivate to work on the root path
  },

  // Guest routes
  {
    path: 'sign-in',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    loadComponent: () => import('./features-modules/auth/sign-in/sign-in.component').then(m => m.SignInComponent)
  },

  {
    path: 'sign-up',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    loadComponent: () => import('./features-modules/auth/sign-up/sign-up.component').then(m => m.SignUpComponent)
  },

  // NOTE: forgot-password / reset-password are registered BOTH top-level (here) and under ':appCode'
  // (see "App guest routes" below) on purpose — the sign-in link uses a relative "../forgot-password"
  // that must resolve from both /sign-in and /:appCode/sign-in. Keep both; do not "de-duplicate".
  {
    path: 'forgot-password',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    loadComponent: () =>
      import('./features-modules/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },

  {
    path: 'reset-password',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    loadComponent: () =>
      import('./features-modules/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
  },

  // Document routes
  {
    path: 'documents',
    canActivate: [guestGuard, documentsContextGuard],
    canActivateChild: [guestGuard],
    loadComponent: () => import('./core/documents/document/document.component').then(m => m.DocumentComponent),
    children: [
      {
        path: ':id',
        loadComponent: () => import('./core/documents/content/document-content.component').then(m => m.DocumentContentComponent)
      },
      {
        path: '',
        loadComponent: () => import('./core/documents/content/document-content.component').then(m => m.DocumentContentComponent)
      }
    ]
  },

  // Admin routes
  {
    path: 'admin',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    component: LayoutComponent,
    resolve: { resolvedData: DataResolver, signalR: signalRResolver },
    data: { routeType: 'Admin' },
    children: [
      {
        path: ':screenId',
        loadComponent: () =>
          import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
            m => m.TabFormRendererComponent
          ),
        data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
      },
      {
        path: '',
        loadComponent: () =>
          import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
            m => m.TabFormRendererComponent
          ),
        data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
      }
    ]
  },

  // App initialize routes
  {
    path: 'initializing',
    canActivate: [guestGuard],
    loadComponent: () => import('./features-modules/auth/initializing/initializing.component').then(m => m.InitializingComponent)
  },

  // Document routes
  {
    path: ':appCode/documents',
    canActivate: [guestGuard, documentsContextGuard],
    canActivateChild: [guestGuard],
    loadComponent: () => import('./core/documents/document/document.component').then(m => m.DocumentComponent),
    children: [
      {
        path: ':id',
        loadComponent: () => import('./core/documents/content/document-content.component').then(m => m.DocumentContentComponent)
      },
      {
        path: '',
        loadComponent: () => import('./core/documents/content/document-content.component').then(m => m.DocumentContentComponent)
      }
    ]
  },

  // App guest routes
  {
    path: ':appCode',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    children: [
      {
        path: 'sign-in',
        loadComponent: () => import('./features-modules/auth/sign-in/sign-in.component').then(m => m.SignInComponent)
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./features-modules/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./features-modules/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
      },
      { path: '', redirectTo: 'sign-in', pathMatch: 'full' }
    ]
  },

  // App routes
  {
    path: ':appCode',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    component: LayoutComponent,
    resolve: { resolvedData: DataResolver, signalR: signalRResolver },
    children: [
      // One component serves both. `adminMode` is declared here rather than passed in, so
      // the screen's mode is fixed by the route it was reached through and cannot be set
      // by a caller — see the comment on ChangePasswordComponent.adminMode.
      {
        path: 'change-password',
        data: { adminMode: false },
        loadComponent: () =>
          import('./features-modules/auth/change-password/change-password.component').then(
            m => m.ChangePasswordComponent
          )
      },
      {
        // Tenant administrators connect their Microsoft organisation. The server enforces the role;
        // anyone else who lands here sees a notice instead of the controls.
        path: 'sign-in-settings',
        loadComponent: () =>
          import('./features-modules/auth/sign-in-settings/sign-in-settings.component').then(
            m => m.SignInSettingsComponent
          )
      },
      {
        // The app's version history (reviews, checks, releases, deployments), read from the versioning
        // service. The service decides who may read and act; the menu shows the entry to app administrators.
        path: 'version-history',
        children: [
          {
            path: 'releases',
            loadComponent: () =>
              import('./features-modules/version-history/releases/releases.component').then(m => m.ReleasesComponent)
          },
          {
            path: 'reviews/:id',
            loadComponent: () =>
              import('./features-modules/version-history/review-detail/review-detail.component').then(
                m => m.ReviewDetailComponent
              )
          },
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./features-modules/version-history/review-list/review-list.component').then(m => m.ReviewListComponent)
          }
        ]
      },
      {
        path: 'admin-reset-password',
        data: { adminMode: true },
        loadComponent: () =>
          import('./features-modules/auth/change-password/change-password.component').then(
            m => m.ChangePasswordComponent
          )
      },
      {
        path: 'screen',
        children: [
          {
            path: ':screenId',
            loadComponent: () =>
              import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
                m => m.TabFormRendererComponent
              ),
            data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
          },
          {
            path: '',
            loadComponent: () =>
              import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
                m => m.TabFormRendererComponent
              ),
            data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
          }
        ]
      }
    ]
  },

  // Organization setup routes
  {
    path: ':appCode',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    resolve: { resolvedData: DataResolver, signalR: signalRResolver },
    children: [
      {
        path: 'org-setup',
        children: [
          {
            path: ':screenId',
            loadComponent: () =>
              import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
                m => m.TabFormRendererComponent
              ),
            data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
          },
          { path: '', redirectTo: '/sign-in', pathMatch: 'full' }
        ]
      }
    ]
  },

  {
    path: '**',
    canActivate: [appCodeRedirectGuard],
    children: [] // This empty children array is necessary for the canActivate to work on the root path
  }
];
